import { useEffect, useState } from 'react';
import { useI18n, type MessageKey } from '../i18n';
import { seconds } from '../lib/format';

/**
 * Real progress, not a spinner.
 *
 * The API names each stage as it starts it; this shows the current one, the
 * ones already done, and how long the run has been going. When the API sends a
 * percentage the bar is determinate; when it does not, the bar animates but
 * the stage name still tells the user what is happening.
 */

const STAGE_KEYS: Record<string, MessageKey> = {
  uploading: 'run.stage.uploading',
  queued: 'run.stage.queued',
  fetching: 'run.stage.fetching',
  parsing: 'run.stage.parsing',
  extracting: 'run.stage.extracting',
  segmenting: 'run.stage.segmenting',
  counting: 'run.stage.counting',
  thresholding: 'run.stage.thresholding',
  normalizing: 'run.stage.normalizing',
  layout: 'run.stage.layout',
  clustering: 'run.stage.clustering',
  writing: 'run.stage.writing',
  done: 'run.stage.done',
};

export interface ProgressPanelProps {
  /** Stage identifier from the API. Unknown values fall back to a generic label. */
  stage: string;
  /** Optional free-text detail from the API, e.g. "page 3 of 12". */
  message?: string;
  /** 0..1 when the API can estimate it. */
  percent?: number;
  /** Stages already passed, in order, for the trail under the bar. */
  history: readonly string[];
  startedAt: number;
  onCancel?: () => void;
}

export function ProgressPanel({
  stage,
  message,
  percent,
  history,
  startedAt,
  onCancel,
}: ProgressPanelProps) {
  const { t } = useI18n();
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 500);
    return () => window.clearInterval(timer);
  }, []);

  const stageLabel = (name: string): string => {
    const key = STAGE_KEYS[name];
    return key ? t(key) : t('run.stage.unknown');
  };

  const elapsed = seconds(now - startedAt);
  const determinate = typeof percent === 'number' && Number.isFinite(percent);

  return (
    <div className="panel">
      <div className="panel__body progress">
        <div className="progress__head">
          <span className="progress__stage" role="status" aria-live="polite">
            {stageLabel(stage)}
            {message ? <span className="muted"> · {message}</span> : null}
          </span>
          <span className="progress__elapsed">{t('run.elapsed', { seconds: elapsed })}</span>
        </div>

        <div
          className="progress__track"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          {...(determinate ? { 'aria-valuenow': Math.round((percent as number) * 100) } : {})}
          aria-label={stageLabel(stage)}
        >
          <div
            className={determinate ? 'progress__fill' : 'progress__fill progress__fill--indeterminate'}
            style={determinate ? { width: `${Math.round((percent as number) * 100)}%` } : undefined}
          />
        </div>

        {history.length > 0 ? (
          <div className="progress__steps">
            {history.map((name) => (
              <span
                key={name}
                className={name === stage ? 'progress__step--current' : 'progress__step--done'}
              >
                {stageLabel(name)}
              </span>
            ))}
          </div>
        ) : null}

        <p className="field__hint">{t('run.hint')}</p>

        {onCancel ? (
          <div className="button-row">
            <button type="button" className="button button--quiet" onClick={onCancel}>
              {t('run.cancel')}
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}
